import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import { generateMobileDeLink } from '../utils/mobileDeLink';
import Spinner from '../components/Spinner';
import './Cars.css';
import './DetailPages.css';

function CarDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(false);
  const [expandedJournal, setExpandedJournal] = useState(false);
  const [expandedReminders, setExpandedReminders] = useState(true);
  const [expandedDefects, setExpandedDefects] = useState(true);
  const [expandedAccidents, setExpandedAccidents] = useState(true);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [showDefectForm, setShowDefectForm] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [editingDefect, setEditingDefect] = useState(null);
  const [editingAccident, setEditingAccident] = useState(null);
  const [noteFormData, setNoteFormData] = useState({ note: '', mileage: '' });
  const [reminderFormData, setReminderFormData] = useState({ title: '', date: '', mileage: '', notes: '', completed: false });
  const [defectFormData, setDefectFormData] = useState({ title: '', description: '', date: '', mileage: '' });
  const [accidentFormData, setAccidentFormData] = useState({ title: '', description: '', date: '', mileage: '' });
  const [maintenancePlans, setMaintenancePlans] = useState([]);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [showDamageReport, setShowDamageReport] = useState(false);
  const [damageReportImages, setDamageReportImages] = useState({});
  const [additionalImages, setAdditionalImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageLabel, setNewImageLabel] = useState('');
  const [damageReportText, setDamageReportText] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadCar(),
        loadRepairs(),
        loadMaintenancePlans(),
        loadTags()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadCar = async () => {
    try {
      const data = await api.get(`/cars/${id}`, tenantId);
      setCar(data);
      if (data.customerId) {
        loadCustomer(data.customerId);
      }
    } catch (error) {
      console.error('Error loading car:', error);
      showNotification('Fehler beim Laden des Autos', 'error');
      navigate('/cars');
    }
  };

  const loadCustomer = async (customerId) => {
    try {
      const data = await api.get(`/customers/${customerId}`, tenantId);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data.filter(r => r.carId === id));
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleAddNote = async () => {
    if (!noteFormData.note.trim()) return;

    try {
      await api.post(`/cars/${id}/notes`, {
        note: noteFormData.note,
        mileage: noteFormData.mileage ? parseInt(noteFormData.mileage) : null,
        date: new Date().toISOString()
      }, tenantId);
      await loadCar();
      setShowNoteForm(false);
      setNoteFormData({ note: '', mileage: '' });
    } catch (error) {
      console.error('Error adding note:', error);
      showNotification('Fehler beim Hinzufügen der Notiz', 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;

    try {
      await api.delete(`/cars/${id}/notes/${noteId}`, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error deleting note:', error);
      showNotification('Fehler beim Löschen der Notiz', 'error');
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderFormData.title.trim() || !reminderFormData.date) return;

    try {
      if (editingReminder) {
        await api.put(`/cars/${id}/reminders/${editingReminder.id}`, {
          title: reminderFormData.title,
          date: reminderFormData.date,
          mileage: reminderFormData.mileage ? parseInt(reminderFormData.mileage) : null,
          notes: reminderFormData.notes,
          completed: reminderFormData.completed
        }, tenantId);
      } else {
        await api.post(`/cars/${id}/reminders`, {
          title: reminderFormData.title,
          date: reminderFormData.date,
          mileage: reminderFormData.mileage ? parseInt(reminderFormData.mileage) : null,
          notes: reminderFormData.notes,
          completed: false
        }, tenantId);
      }
      await loadCar();
      setShowReminderForm(false);
      setEditingReminder(null);
      setReminderFormData({ title: '', date: '', mileage: '', notes: '', completed: false });
    } catch (error) {
      console.error('Error saving reminder:', error);
      showNotification('Fehler beim Speichern der Erinnerung', 'error');
    }
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setReminderFormData({
      title: reminder.title || '',
      date: reminder.date ? reminder.date.split('T')[0] : '',
      mileage: reminder.mileage ? String(reminder.mileage) : '',
      notes: reminder.notes || '',
      completed: reminder.completed || false
    });
    setShowReminderForm(true);
  };

  const handleToggleReminderComplete = async (reminder) => {
    try {
      await api.put(`/cars/${id}/reminders/${reminder.id}`, {
        title: reminder.title,
        date: reminder.date,
        mileage: reminder.mileage,
        notes: reminder.notes,
        completed: !reminder.completed
      }, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error toggling reminder:', error);
      showNotification('Fehler beim Aktualisieren der Erinnerung', 'error');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Erinnerung wirklich löschen?')) return;

    try {
      await api.delete(`/cars/${id}/reminders/${reminderId}`, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showNotification('Fehler beim Löschen der Erinnerung', 'error');
    }
  };

  const handleSaveDefect = async () => {
    if (!defectFormData.title) {
      showNotification('Bitte geben Sie einen Titel ein', 'error');
      return;
    }

    try {
      if (editingDefect) {
        await api.put(`/cars/${id}/known-defects/${editingDefect.id}`, defectFormData, tenantId);
      } else {
        await api.post(`/cars/${id}/known-defects`, defectFormData, tenantId);
      }
      await loadCar();
      setShowDefectForm(false);
      setEditingDefect(null);
      setDefectFormData({ title: '', description: '', date: '', mileage: '' });
    } catch (error) {
      console.error('Error saving defect:', error);
      showNotification('Fehler beim Speichern des Defekts', 'error');
    }
  };

  const handleEditDefect = (defect) => {
    setEditingDefect(defect);
    setDefectFormData({
      title: defect.title || '',
      description: defect.description || '',
      date: defect.date ? defect.date.split('T')[0] : '',
      mileage: defect.mileage ? String(defect.mileage) : ''
    });
    setShowDefectForm(true);
  };

  const handleDeleteDefect = async (defectId) => {
    if (!window.confirm('Bekannten Defekt wirklich löschen?')) return;

    try {
      await api.delete(`/cars/${id}/known-defects/${defectId}`, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error deleting defect:', error);
      showNotification('Fehler beim Löschen des Defekts', 'error');
    }
  };

  const handleCreateRepairFromDefect = (defect) => {
    navigate(`/repairs/create?carId=${id}&defectId=${defect.id}&description=${encodeURIComponent(defect.description || defect.title)}&mileage=${defect.mileage || car.mileage || ''}&date=${defect.date || ''}`);
  };

  const handleSaveAccident = async () => {
    if (!accidentFormData.title) {
      showNotification('Bitte geben Sie einen Titel ein', 'error');
      return;
    }

    try {
      if (editingAccident) {
        await api.put(`/cars/${id}/accidents/${editingAccident.id}`, accidentFormData, tenantId);
      } else {
        await api.post(`/cars/${id}/accidents`, accidentFormData, tenantId);
      }
      await loadCar();
      setShowAccidentForm(false);
      setEditingAccident(null);
      setAccidentFormData({ title: '', description: '', date: '', mileage: '' });
    } catch (error) {
      console.error('Error saving accident:', error);
      showNotification('Fehler beim Speichern des Unfalls', 'error');
    }
  };

  const handleEditAccident = (accident) => {
    setEditingAccident(accident);
    setAccidentFormData({
      title: accident.title || '',
      description: accident.description || '',
      date: accident.date ? accident.date.split('T')[0] : '',
      mileage: accident.mileage ? String(accident.mileage) : ''
    });
    setShowAccidentForm(true);
  };

  const handleDeleteAccident = async (accidentId) => {
    if (!window.confirm('Unfall wirklich löschen?')) return;

    try {
      await api.delete(`/cars/${id}/accidents/${accidentId}`, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error deleting accident:', error);
      showNotification('Fehler beim Löschen des Unfalls', 'error');
    }
  };

  const handleToggleAccidentVehicle = async (e) => {
    try {
      await api.put(`/cars/${id}`, { accidentVehicle: e.target.checked }, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error updating accident vehicle status:', error);
      showNotification('Fehler beim Aktualisieren', 'error');
    }
  };

  const loadMaintenancePlans = async () => {
    try {
      const data = await api.get('/maintenance-plans', tenantId);
      setMaintenancePlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading maintenance plans:', error);
      setMaintenancePlans([]);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const handleApplyPlan = async (planId) => {
    try {
      await api.post(`/maintenance-plans/${planId}/apply-to-car/${id}`, {}, tenantId);
      await loadCar();
      setShowPlanSelector(false);
    } catch (error) {
      console.error('Error applying plan:', error);
      showNotification('Fehler beim Anwenden des Wartungsplans', 'error');
    }
  };

  const handleRemovePlan = async (planId) => {
    if (!window.confirm('Wartungsplan wirklich entfernen?')) return;
    
    try {
      await api.delete(`/maintenance-plans/${planId}/remove-from-car/${id}`, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error removing plan:', error);
      showNotification('Fehler beim Entfernen des Wartungsplans', 'error');
    }
  };


  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('Bitte wählen Sie eine Bilddatei aus'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        reject(new Error('Die Datei ist zu groß. Maximal 5MB erlaubt.'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 1200;
          const maxHeight = 1200;
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          let base64String = canvas.toDataURL('image/jpeg', 0.7);
          let quality = 0.7;
          while (base64String.length > 1.5 * 1024 * 1024 && quality > 0.3) {
            quality -= 0.1;
            base64String = canvas.toDataURL('image/jpeg', quality);
          }
          if (base64String.length > 2 * 1024 * 1024) {
            reject(new Error('Bild ist zu groß auch nach Kompression. Bitte wählen Sie ein kleineres Bild.'));
            return;
          }
          resolve(base64String);
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
      reader.readAsDataURL(file);
    });
  };

  const handleCarImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const compressedImage = await compressImage(file);
      await api.put(`/cars/${id}`, { image: compressedImage }, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error uploading car image:', error);
      showNotification(error.message || 'Fehler beim Hochladen des Bildes', 'error');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveCarImage = async () => {
    if (!window.confirm('Bild wirklich entfernen?')) return;
    try {
      await api.put(`/cars/${id}`, { image: null }, tenantId);
      await loadCar();
    } catch (error) {
      console.error('Error removing car image:', error);
      showNotification('Fehler beim Entfernen des Bildes', 'error');
    }
  };

  const handleDamageReportImageUpload = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const compressedImage = await compressImage(file);
      const newImages = { ...damageReportImages, [side]: compressedImage };
      setDamageReportImages(newImages);
    } catch (error) {
      console.error('Error uploading damage report image:', error);
      showNotification(error.message || 'Fehler beim Hochladen des Bildes', 'error');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!newImageLabel.trim()) {
      showNotification('Bitte geben Sie eine Beschreibung für das Bild ein', 'error');
      e.target.value = '';
      return;
    }
    
    setUploadingImage(true);
    try {
      const compressedImage = await compressImage(file);
      const newImage = {
        id: `additional-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        label: newImageLabel.trim(),
        image: compressedImage
      };
      setAdditionalImages([...additionalImages, newImage]);
      setNewImageLabel('');
    } catch (error) {
      console.error('Error uploading additional image:', error);
      showNotification(error.message || 'Fehler beim Hochladen des Bildes', 'error');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveAdditionalImage = (imageId) => {
    setAdditionalImages(additionalImages.filter(img => img.id !== imageId));
  };

  const handleSaveDamageReport = async () => {
    try {
      const imagesData = { ...damageReportImages };
      if (additionalImages.length > 0) {
        imagesData.additional = additionalImages;
      }
      await api.put(`/cars/${id}/damage-report`, { 
        images: imagesData,
        text: damageReportText.trim() || null
      }, tenantId);
      await loadCar();
      setShowDamageReport(false);
      setDamageReportImages({});
      setAdditionalImages([]);
      setNewImageLabel('');
      setDamageReportText('');
    } catch (error) {
      console.error('Error saving damage report:', error);
      showNotification('Fehler beim Speichern des Schadensprotokolls', 'error');
    }
  };

  const handleDeleteDamageReport = async () => {
    if (!window.confirm('Schadensprotokoll wirklich löschen?')) return;
    try {
      await api.delete(`/cars/${id}/damage-report`, tenantId);
      await loadCar();
      setShowDamageReport(false);
      setDamageReportImages({});
      setAdditionalImages([]);
      setNewImageLabel('');
    } catch (error) {
      console.error('Error deleting damage report:', error);
      showNotification('Fehler beim Löschen des Schadensprotokolls', 'error');
    }
  };

  useEffect(() => {
    if (car && car.damageReport) {
      if (car.damageReport.images) {
        const images = car.damageReport.images;
        const standardSides = ['front', 'rear', 'left', 'right', 'roof', 'interior'];
        const standardImages = {};
        standardSides.forEach(side => {
          if (images[side]) {
            standardImages[side] = images[side];
          }
        });
        setDamageReportImages(standardImages);
        setAdditionalImages(images.additional || []);
      } else {
        setDamageReportImages({});
        setAdditionalImages([]);
      }
      setDamageReportText(car.damageReport.text || '');
    } else {
      setDamageReportImages({});
      setAdditionalImages([]);
      setDamageReportText('');
    }
  }, [car]);

  if (loading) {
    return <Spinner />;
  }

  if (!car) {
    return <div>Auto nicht gefunden</div>;
  }

  const carRepairs = repairs.sort((a, b) => new Date(b.date) - new Date(a.date));
  const totalRepairAmount = carRepairs.reduce((sum, r) => sum + (r.totalAmount || 0), 0);

  return (
    <div className="car-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/cars')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{car.make} {car.model} {car.year}</h1>
        <button type="button" onClick={() => navigate(`/cars/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="car-details-grid">
        <div className="detail-section">
          <h2>Fahrzeugbild</h2>
          {car.image ? (
            <div style={{ marginBottom: '1rem' }}>
              <img 
                src={car.image} 
                alt={`${car.make} ${car.model}`}
                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
              <div>
                <label className="btn-small btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                  {uploadingImage ? 'Wird hochgeladen...' : 'Bild ändern'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCarImageUpload}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleRemoveCarImage}
                  className="btn-small btn-secondary"
                  style={{ marginLeft: '0.5rem' }}
                >
                  Bild entfernen
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label className="btn-small" style={{ cursor: 'pointer', display: 'inline-block' }}>
                {uploadingImage ? 'Wird hochgeladen...' : 'Bild hochladen'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCarImageUpload}
                  disabled={uploadingImage}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h2>Fahrzeugdaten</h2>
          <p><strong>Kennzeichen:</strong> {car.licensePlate || '-'}</p>
          <p><strong>VIN:</strong> {car.vin || '-'}</p>
          {tenant?.taxSystem === 'CH' ? (
            <p><strong>Typenschein:</strong> {car.typenschein || '-'}</p>
          ) : (
            <>
              <p><strong>HSN:</strong> {car.hsn || '-'}</p>
              <p><strong>TSN:</strong> {car.tsn || '-'}</p>
            </>
          )}
          {car.engineCode && <p><strong>Motorkennbuchstabe:</strong> {car.engineCode}</p>}
          {car.fuelType && <p><strong>Kraftstoffart:</strong> {car.fuelType === 'BENZIN' ? 'Benzin' : car.fuelType === 'DIESEL' ? 'Diesel' : car.fuelType === 'ELEKTRO' ? 'Elektro' : car.fuelType === 'HYBRID' ? 'Hybrid' : car.fuelType === 'LPG' ? 'LPG' : car.fuelType === 'CNG' ? 'CNG/Erdgas' : car.fuelType}</p>}
          {car.transmission && <p><strong>Getriebeart:</strong> {car.transmission === 'MANUAL' ? 'Manuell' : car.transmission === 'AUTOMATIC' ? 'Automatik' : car.transmission}</p>}
          <p><strong>Kilometerstand:</strong> {car.mileage?.toLocaleString('de-DE') || 0} km</p>
          
          {car.tags && car.tags.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <strong>Tags:</strong>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {car.tags.map(tagId => {
                  const tag = tags.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <span
                      key={tagId}
                      style={{
                        fontSize: '0.7rem',
                        padding: '0.15rem 0.4rem',
                        borderRadius: '20px',
                        backgroundColor: tag.color,
                        color: '#000',
                        border: `1.5px solid ${tag.color}`,
                        fontWeight: 600
                      }}
                    >
                      {tag.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          
          {(() => {
            const mobileDeUrl = generateMobileDeLink(car);
            return mobileDeUrl ? (
              <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Vergleichbare Fahrzeuge</h3>
                <a 
                  href={mobileDeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-small"
                  style={{ display: 'inline-block', textDecoration: 'none' }}
                >
                  Auf mobile.de suchen →
                </a>
              </div>
            ) : null;
          })()}
        </div>

        <div className="detail-section">
          <h2>Wartungspläne</h2>
          
          {(() => {
            const carMaintenancePlans = car.maintenancePlans || [];
            const legacyPlanId = car.maintenancePlanId;
            
            if (legacyPlanId && carMaintenancePlans.length === 0) {
              carMaintenancePlans.push({
                planId: legacyPlanId,
                appliedDate: car.maintenancePlanAppliedDate,
                startMileage: car.maintenancePlanStartMileage
              });
            }
            
            return carMaintenancePlans.length > 0 ? (
              <div style={{ marginBottom: '1rem' }}>
                {carMaintenancePlans.map((carPlan, index) => {
                  const plan = maintenancePlans.find(p => p.id === carPlan.planId);
                  if (!plan) return null;
                  
                  return (
                    <div key={carPlan.planId || index} style={{ marginBottom: '0.75rem', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{plan.name || 'Unbekannter Plan'}</strong>
                          {carPlan.appliedDate && (
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '0.25rem' }}>
                              Angewendet: {formatDate(carPlan.appliedDate)}
                              {carPlan.startMileage && (
                                <span style={{ marginLeft: '1rem' }}>
                                  (bei {carPlan.startMileage.toLocaleString('de-DE')} km)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemovePlan(carPlan.planId)}
                          className="btn-small btn-secondary"
                        >
                          Plan entfernen
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null;
          })()}
          
          <div style={{ marginBottom: '1rem' }}>
            {showPlanSelector ? (
              <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Wartungsplan auswählen</h3>
                {maintenancePlans.filter(p => p.active).length === 0 ? (
                  <p style={{ color: '#666' }}>Keine aktiven Wartungspläne verfügbar</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {maintenancePlans.filter(p => {
                      const carMaintenancePlans = car.maintenancePlans || [];
                      const legacyPlanId = car.maintenancePlanId;
                      const appliedPlanIds = carMaintenancePlans.map(mp => mp.planId);
                      if (legacyPlanId && !appliedPlanIds.includes(legacyPlanId)) {
                        appliedPlanIds.push(legacyPlanId);
                      }
                      return p.active && !appliedPlanIds.includes(p.id);
                    }).map(plan => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handleApplyPlan(plan.id)}
                        className="btn-small"
                        style={{ textAlign: 'left', padding: '0.75rem' }}
                      >
                        <strong>{plan.name}</strong>
                        {plan.description && (
                          <div style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
                            {plan.description}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowPlanSelector(false)}
                  className="btn-small btn-secondary"
                  style={{ marginTop: '1rem' }}
                >
                  Abbrechen
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowPlanSelector(true)}
                className="btn-small"
              >
                Wartungsplan hinzufügen
              </button>
            )}
          </div>

          <div 
            className="car-journal-header"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedReminders(!expandedReminders);
            }}
            style={{ cursor: 'pointer', marginBottom: '0.5rem', marginTop: '1rem' }}
          >
            <span>Wartungserinnerungen ({(car.maintenanceReminders || []).length})</span>
            <span className="journal-toggle-arrow">{expandedReminders ? '▼' : '▶'}</span>
          </div>
          {expandedReminders && (
            <div>
              {(car.maintenanceReminders || []).length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Keine Erinnerungen vorhanden</p>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  {(car.maintenanceReminders || []).map(reminder => {
                    const reminderDate = new Date(reminder.date);
                    const now = new Date();
                    const daysUntil = Math.ceil((reminderDate - now) / (1000 * 60 * 60 * 24));
                    const isOverdue = reminderDate < now && !reminder.completed;
                    const isUpcoming = daysUntil <= 7 && daysUntil >= 0 && !reminder.completed;
                    
                    return (
                      <div 
                        key={reminder.id} 
                        style={{ 
                          marginBottom: '0.75rem', 
                          padding: '0.75rem', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          backgroundColor: reminder.completed ? '#f5f5f5' : (isOverdue ? '#ffebee' : (isUpcoming ? '#fff8e1' : '#fff')),
                          borderLeft: reminder.completed ? '4px solid #4caf50' : (isOverdue ? '4px solid #d32f2f' : (isUpcoming ? '4px solid #ff9800' : '4px solid #2196f3')),
                          opacity: reminder.completed ? 0.7 : 1
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <strong style={{ textDecoration: reminder.completed ? 'line-through' : 'none' }}>
                                {reminder.title || 'Wartungserinnerung'}
                              </strong>
                              {reminder.completed && (
                                <span style={{ color: '#4caf50', fontSize: '0.85rem' }}>✓ Erledigt</span>
                              )}
                              {isOverdue && !reminder.completed && (
                                <span style={{ color: '#d32f2f', fontSize: '0.85rem', fontWeight: 'bold' }}>⚠️ Überfällig</span>
                              )}
                              {isUpcoming && !reminder.completed && (
                                <span style={{ color: '#f57c00', fontSize: '0.85rem', fontWeight: 'bold' }}>⚠️ Anstehend</span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.9em', color: '#666' }}>
                              <span>📅 {formatDate(reminder.date)}</span>
                              {reminder.mileage && (
                                <span style={{ marginLeft: '1rem' }}>📏 {reminder.mileage.toLocaleString('de-DE')} km</span>
                              )}
                              {reminder.completedDate && (
                                <span style={{ marginLeft: '1rem', color: '#4caf50' }}>
                                  Erledigt: {formatDate(reminder.completedDate)}
                                </span>
                              )}
                            </div>
                            {reminder.notes && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                {reminder.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              type="button"
                              onClick={() => handleEditReminder(reminder)}
                              className="btn-small"
                              style={{ fontSize: '0.8rem' }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleReminderComplete(reminder)}
                              className="btn-small"
                              style={{ fontSize: '0.8rem', backgroundColor: reminder.completed ? '#ff9800' : '#4caf50', color: 'white' }}
                            >
                              {reminder.completed ? 'Rückgängig' : 'Erledigt'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="btn-small btn-danger"
                              style={{ fontSize: '0.8rem' }}
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {showReminderForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingReminder ? 'Erinnerung bearbeiten' : 'Neue Erinnerung hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={reminderFormData.title}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, title: e.target.value })}
                      placeholder="z.B. Ölwechsel, Inspektion, TÜV"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum *</label>
                    <input
                      type="date"
                      value={reminderFormData.date}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand (optional)</label>
                    <input
                      type="number"
                      value={reminderFormData.mileage}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, mileage: e.target.value })}
                      placeholder="z.B. 50000"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Notizen (optional)</label>
                    <textarea
                      value={reminderFormData.notes}
                      onChange={(e) => setReminderFormData({ ...reminderFormData, notes: e.target.value })}
                      placeholder="Zusätzliche Informationen..."
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveReminder}
                      className="btn-small"
                      disabled={!reminderFormData.title || !reminderFormData.date}
                    >
                      {editingReminder ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowReminderForm(false);
                        setEditingReminder(null);
                        setReminderFormData({ title: '', date: '', mileage: '', notes: '', completed: false });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowReminderForm(true)}
                  className="btn-small"
                >
                  Erinnerung hinzufügen
                </button>
              )}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h2>Schadensprotokoll</h2>
          {car.damageReport && (car.damageReport.images && Object.keys(car.damageReport.images).length > 0 || car.damageReport.text) ? (
            <div>
              <div style={{ marginBottom: '1rem', fontSize: '0.9em', color: '#666' }}>
                Erstellt: {formatDate(car.damageReport.date)}
              </div>
              {car.damageReport.text && (
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Notizen:</div>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#333' }}>{car.damageReport.text}</div>
                </div>
              )}
              {car.damageReport.images && Object.keys(car.damageReport.images).length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  {['front', 'rear', 'left', 'right', 'roof', 'interior'].map(side => {
                    const sideLabels = {
                      front: 'Front',
                      rear: 'Heck',
                      left: 'Links',
                      right: 'Rechts',
                      roof: 'Dach',
                      interior: 'Innenraum'
                    };
                    const image = car.damageReport.images[side];
                    return (
                      <div key={side} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{sideLabels[side]}</div>
                        {image ? (
                          <img 
                            src={image} 
                            alt={sideLabels[side]}
                            style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            Kein Bild
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {car.damageReport.images.additional && car.damageReport.images.additional.map(additional => (
                    <div key={additional.id} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{additional.label || 'Zusätzliches Bild'}</div>
                      {additional.image ? (
                        <img 
                          src={additional.image} 
                          alt={additional.label || 'Zusätzliches Bild'}
                          style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          Kein Bild
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDamageReport(true);
                    const images = car.damageReport.images || {};
                    const standardSides = ['front', 'rear', 'left', 'right', 'roof', 'interior'];
                    const standardImages = {};
                    standardSides.forEach(side => {
                      if (images[side]) {
                        standardImages[side] = images[side];
                      }
                    });
                    setDamageReportImages(standardImages);
                    setAdditionalImages(images.additional || []);
                    setDamageReportText(car.damageReport.text || '');
                  }}
                  className="btn-small"
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  onClick={handleDeleteDamageReport}
                  className="btn-small btn-secondary"
                >
                  Löschen
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ color: '#666', marginBottom: '1rem' }}>Kein Schadensprotokoll vorhanden</p>
              <button
                type="button"
                onClick={() => {
                  setShowDamageReport(true);
                  setDamageReportImages({});
                }}
                className="btn-small"
              >
                Schadensprotokoll erstellen
              </button>
            </div>
          )}
          {showDamageReport && (
            <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Schadensprotokoll bearbeiten</h3>
              
              <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '2px solid #ddd' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Notizen / Beschreibung</label>
                <textarea
                  value={damageReportText}
                  onChange={(e) => setDamageReportText(e.target.value)}
                  placeholder="Beschreibung der Schäden, Notizen, etc..."
                  rows={4}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                {['front', 'rear', 'left', 'right', 'roof', 'interior'].map(side => {
                  const sideLabels = {
                    front: 'Front',
                    rear: 'Heck',
                    left: 'Links',
                    right: 'Rechts',
                    roof: 'Dach',
                    interior: 'Innenraum'
                  };
                  const image = damageReportImages[side];
                  return (
                    <div key={side} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{sideLabels[side]}</div>
                      {image ? (
                        <div>
                          <img 
                            src={image} 
                            alt={sideLabels[side]}
                            style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                          />
                          <label className="btn-small btn-secondary" style={{ cursor: 'pointer', display: 'block', textAlign: 'center' }}>
                            {uploadingImage ? 'Wird hochgeladen...' : 'Bild ändern'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleDamageReportImageUpload(e, side)}
                              disabled={uploadingImage}
                              style={{ display: 'none' }}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = { ...damageReportImages };
                              delete newImages[side];
                              setDamageReportImages(newImages);
                            }}
                            className="btn-small btn-secondary"
                            style={{ width: '100%', marginTop: '0.25rem' }}
                          >
                            Entfernen
                          </button>
                        </div>
                      ) : (
                        <label className="btn-small" style={{ cursor: 'pointer', display: 'block', textAlign: 'center' }}>
                          {uploadingImage ? 'Wird hochgeladen...' : 'Bild hochladen'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleDamageReportImageUpload(e, side)}
                            disabled={uploadingImage}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #ddd' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Zusätzliche Bilder</h4>
                {additionalImages.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                    {additionalImages.map(additional => (
                      <div key={additional.id} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{additional.label || 'Zusätzliches Bild'}</div>
                        {additional.image ? (
                          <div>
                            <img 
                              src={additional.image} 
                              alt={additional.label || 'Zusätzliches Bild'}
                              style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveAdditionalImage(additional.id)}
                              className="btn-small btn-secondary"
                              style={{ width: '100%' }}
                            >
                              Entfernen
                            </button>
                          </div>
                        ) : (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            Kein Bild
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Beschreibung</label>
                    <input
                      type="text"
                      value={newImageLabel}
                      onChange={(e) => setNewImageLabel(e.target.value)}
                      placeholder="z.B. Kratzer an der Tür, Delle am Kotflügel"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <label className="btn-small" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    {uploadingImage ? 'Wird hochgeladen...' : 'Bild hinzufügen'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAdditionalImageUpload}
                      disabled={uploadingImage || !newImageLabel.trim()}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={handleSaveDamageReport}
                  className="btn-small"
                  disabled={uploadingImage}
                >
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDamageReport(false);
                    const images = car.damageReport?.images || {};
                    const standardSides = ['front', 'rear', 'left', 'right', 'roof', 'interior'];
                    const standardImages = {};
                    standardSides.forEach(side => {
                      if (images[side]) {
                        standardImages[side] = images[side];
                      }
                    });
                    setDamageReportImages(standardImages);
                    setAdditionalImages(images.additional || []);
                    setNewImageLabel('');
                    setDamageReportText(car.damageReport?.text || '');
                  }}
                  className="btn-small btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Bekannte Defekte</h2>
            {!showDefectForm && (
              <button
                onClick={() => {
                  setShowDefectForm(true);
                  setExpandedDefects(true);
                }}
                className="btn-small"
              >
                Defekt hinzufügen
              </button>
            )}
          </div>
          <div 
            className="car-journal-header"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedDefects(!expandedDefects);
            }}
            style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
          >
            <span>Defekte ({(car.knownDefects || []).length})</span>
            <span className="journal-toggle-arrow">{expandedDefects ? '▼' : '▶'}</span>
          </div>
          {expandedDefects && (
            <div>
              {showDefectForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9', marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingDefect ? 'Defekt bearbeiten' : 'Neuen Defekt hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={defectFormData.title}
                      onChange={(e) => setDefectFormData({ ...defectFormData, title: e.target.value })}
                      placeholder="z.B. Bremsen quietschen, Motor läuft unrund"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Beschreibung</label>
                    <textarea
                      value={defectFormData.description}
                      onChange={(e) => setDefectFormData({ ...defectFormData, description: e.target.value })}
                      placeholder="Detaillierte Beschreibung des Defekts..."
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum (optional)</label>
                    <input
                      type="date"
                      value={defectFormData.date}
                      onChange={(e) => setDefectFormData({ ...defectFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand (optional)</label>
                    <input
                      type="number"
                      value={defectFormData.mileage}
                      onChange={(e) => setDefectFormData({ ...defectFormData, mileage: e.target.value })}
                      placeholder="z.B. 50000"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveDefect}
                      className="btn-small"
                      disabled={!defectFormData.title}
                    >
                      {editingDefect ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDefectForm(false);
                        setEditingDefect(null);
                        setDefectFormData({ title: '', description: '', date: '', mileage: '' });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : null}
              {(car.knownDefects || []).length === 0 && !showDefectForm ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Keine bekannten Defekte vorhanden</p>
              ) : (car.knownDefects || []).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  {(car.knownDefects || []).map(defect => (
                    <div key={defect.id} style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: 0, marginBottom: '0.25rem' }}>{defect.title}</h4>
                          {defect.description && (
                            <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9em' }}>{defect.description}</p>
                          )}
                          <div style={{ fontSize: '0.85em', color: '#666', display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                            {defect.date && <span>Datum: {formatDate(defect.date)}</span>}
                            {defect.mileage && <span>Kilometerstand: {defect.mileage.toLocaleString('de-DE')} km</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => handleCreateRepairFromDefect(defect)}
                            className="btn-small"
                            style={{ backgroundColor: '#2196f3', color: 'white' }}
                          >
                            Reparatur erstellen
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditDefect(defect)}
                            className="btn-small btn-secondary"
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDefect(defect.id)}
                            className="btn-small btn-danger"
                          >
                            Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h2>Unfälle</h2>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={car.accidentVehicle || false}
                onChange={handleToggleAccidentVehicle}
              />
              <span style={{ fontWeight: 'bold' }}>Unfallfahrzeug</span>
            </label>
          </div>
          <div 
            className="car-journal-header"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedAccidents(!expandedAccidents);
            }}
            style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
          >
            <span>Bekannte Unfälle ({(car.accidents || []).length})</span>
            <span className="journal-toggle-arrow">{expandedAccidents ? '▼' : '▶'}</span>
          </div>
          {expandedAccidents && (
            <div>
              {(car.accidents || []).length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Keine bekannten Unfälle vorhanden</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  {(car.accidents || []).map(accident => (
                    <div key={accident.id} style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: 0, marginBottom: '0.25rem' }}>{accident.title}</h4>
                          {accident.description && (
                            <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9em' }}>{accident.description}</p>
                          )}
                          <div style={{ fontSize: '0.85em', color: '#666', display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                            {accident.date && <span>Datum: {formatDate(accident.date)}</span>}
                            {accident.mileage && <span>Kilometerstand: {accident.mileage.toLocaleString('de-DE')} km</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => handleEditAccident(accident)}
                            className="btn-small btn-secondary"
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAccident(accident.id)}
                            className="btn-small btn-danger"
                          >
                            Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showAccidentForm ? (
                <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                    {editingAccident ? 'Unfall bearbeiten' : 'Neuen Unfall hinzufügen'}
                  </h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Titel *</label>
                    <input
                      type="text"
                      value={accidentFormData.title}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, title: e.target.value })}
                      placeholder="z.B. Frontunfall, Heckkollision"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Beschreibung</label>
                    <textarea
                      value={accidentFormData.description}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, description: e.target.value })}
                      placeholder="Detaillierte Beschreibung des Unfalls..."
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Datum (optional)</label>
                    <input
                      type="date"
                      value={accidentFormData.date}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, date: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>Kilometerstand (optional)</label>
                    <input
                      type="number"
                      value={accidentFormData.mileage}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, mileage: e.target.value })}
                      placeholder="z.B. 50000"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSaveAccident}
                      className="btn-small"
                      disabled={!accidentFormData.title}
                    >
                      {editingAccident ? 'Speichern' : 'Hinzufügen'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAccidentForm(false);
                        setEditingAccident(null);
                        setAccidentFormData({ title: '', description: '', date: '', mileage: '' });
                      }}
                      className="btn-small btn-secondary"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAccidentForm(true)}
                  className="btn-small"
                >
                  Unfall hinzufügen
                </button>
              )}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h2>Kunde</h2>
          {customer ? (
            <p>
              <span 
                className="link-button"
                onClick={() => navigate(`/customers/${customer.id}`)}
              >
                {customer.firstName} {customer.lastName}
              </span>
            </p>
          ) : (
            <p>-</p>
          )}
        </div>
      </div>

      <div className="repair-history-section">
        <div 
          className="repair-history-header"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedHistory(!expandedHistory);
          }}
        >
          <span>Reparaturhistorie ({carRepairs.length})</span>
          <span className="toggle-arrow">{expandedHistory ? '▼' : '▶'}</span>
        </div>
        {expandedHistory && (
          <div className="repair-history">
            {carRepairs.length === 0 ? (
              <p>Keine Reparaturen</p>
            ) : (
              <>
                <p><strong>Gesamtbetrag:</strong> {formatCurrency(totalRepairAmount)}</p>
                {carRepairs.map(repair => (
                  <div 
                    key={repair.id} 
                    className="repair-history-item clickable"
                    onClick={() => navigate(`/repairs/${repair.id}`)}
                  >
                    <div>
                      <strong>{repair.repairNumber || '-'}</strong> - {formatDate(repair.date)}
                      {repair.mileage && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                          ({repair.mileage.toLocaleString('de-DE')} km)
                        </span>
                      )}
                      {repair.description && (
                        <div style={{ marginTop: '0.25rem', fontSize: '0.9em', color: '#666' }}>
                          {repair.description}
                        </div>
                      )}
                    </div>
                    <div>{formatCurrency(repair.totalAmount || 0)}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <div className="car-journal-section">
        <div 
          className="car-journal-header"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedJournal(!expandedJournal);
          }}
        >
          <span>Bemerkungen</span>
          <span className="journal-toggle-arrow">{expandedJournal ? '▼' : '▶'}</span>
        </div>
        {expandedJournal && (
          <div className="journal-content">
            {car.notes && car.notes.length > 0 ? (
              <div className="journal-list">
                {car.notes.map((note, idx) => (
                  <div key={idx} className="journal-item">
                    <div className="journal-item-header">
                      <span>{formatDate(note.date)}</span>
                      {note.mileage && <span>{note.mileage.toLocaleString('de-DE')} km</span>}
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(note.id)}
                        className="btn-small btn-danger"
                      >
                        Löschen
                      </button>
                    </div>
                    <p>{note.note}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>Keine Bemerkungen</p>
            )}
            {showNoteForm ? (
              <div className="note-form">
                <textarea
                  value={noteFormData.note}
                  onChange={(e) => setNoteFormData({ ...noteFormData, note: e.target.value })}
                  placeholder="Bemerkung eingeben..."
                  rows={3}
                />
                <input
                  type="text"
                  value={noteFormData.mileage}
                  onChange={(e) => setNoteFormData({ ...noteFormData, mileage: e.target.value })}
                  placeholder="Kilometerstand (optional)"
                />
                <div>
                  <button type="button" onClick={handleAddNote} className="btn-small">Hinzufügen</button>
                  <button type="button" onClick={() => setShowNoteForm(false)} className="btn-small btn-secondary">Abbrechen</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNoteForm(true)}
                className="btn-small"
              >
                Notiz hinzufügen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CarDetail;

